import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate, formatEUR, fullName } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Undo2, Plus, CheckCircle2, RotateCcw, Trash2, Pencil, Loader2, ReceiptText, Landmark, AlertTriangle } from 'lucide-react';
import { PatientCombobox } from '../components/PatientCombobox';

// Los mismos que un cobro: la devolución se lee en la ficha con el mismo
// idioma que el pago que la originó.
const METODOS = ['Efectivo', 'Tarjeta', 'Transferencia', 'Bizum', 'Enlace de pago', 'Financiación'];

const FILTROS = [['pendiente', 'Pendientes'], ['hecha', 'Hechas'], ['todas', 'Todas']];

/**
 * Confirmación de borrado (2026-09-17). Esto es dinero: antes de que
 * desaparezca una ficha se enseña el importe, de quién es, el concepto y
 * cuándo se apuntó, que es justo lo que queda escrito en Control rastreo. Un
 * `window.confirm` no llega: no se lee el importe y no se puede releer.
 */
function ConfirmarBorrado({ item, onCancel, onConfirm, busy, titulo, importe, concepto, aviso, testid }) {
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-4 h-4" /> {titulo}
          </DialogTitle>
        </DialogHeader>
        {item && (
          <div className="space-y-2 text-sm">
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <div className="text-2xl font-semibold tabular-nums text-destructive" data-testid={`${testid}-importe`}>
                {importe ? formatEUR(importe) : 'Sin importe'}
              </div>
              <div className="font-medium">{item.patient_name}</div>
              {concepto ? <div className="text-xs text-muted-foreground whitespace-pre-line">{concepto}</div> : null}
              <div className="text-xs text-muted-foreground">
                Apuntada el {formatDate((item.created_at || '').slice(0, 10))}
                {item.created_by_name ? ` por ${item.created_by_name}` : ''}
              </div>
            </div>
            {aviso ? <p className="text-xs text-muted-foreground">{aviso}</p> : null}
            <p className="text-xs text-muted-foreground">
              Se borra del listado, pero queda escrito en Control de rastreo qué era y quién lo borró.
            </p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy} data-testid={`${testid}-confirmar`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FiltroBarra({ filtro, setFiltro, opciones = FILTROS, testid }) {
  return (
    <div className="cp-card p-3 mb-4 flex gap-1" data-testid={testid}>
      {opciones.map(([v, l]) => (
        <Button key={v} size="sm" variant={filtro === v ? 'default' : 'ghost'}
          className={filtro === v ? 'bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white' : ''}
          onClick={() => setFiltro(v)}>
          {l}
        </Button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Devoluciones (2026-08-27: campos obligatorios — nombre y apellidos,
// importe, motivo y cómo pagó el paciente; con transferencia, su cuenta).
// ---------------------------------------------------------------------------
function TabDevoluciones() {
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState('pendiente');
  const [alta, setAlta] = useState(false);
  const FORM_VACIO = {
    patient_id: '', nombre: '', apellido1: '', apellido2: '',
    importe: '', motivo: '', forma_pago_original: '', cuenta_origen: '',
  };
  const [form, setForm] = useState(FORM_VACIO);
  const [busy, setBusy] = useState(false);
  // Corregir una devolución mal apuntada (2026-09-17). El permiso
  // del servidor ya estaba abierto a gestión: lo que faltaba era esta puerta.
  const [editando, setEditando] = useState(null);
  // `patient_id` entra aquí en la segunda vuelta (2026-09-17): el apunte de
  // dinero de la ficha del paciente cuelga del id, no del nombre, así que
  // corregir el paciente es cambiar de FICHA, no reescribir el texto.
  const [edit, setEdit] = useState({
    patient_id: '', patient_name: '', importe: '', motivo: '',
    forma_pago_original: '', cuenta_origen: '',
  });
  const [borrando, setBorrando] = useState(null);
  // Marcar como devuelta pide SIEMPRE el método: sin él no se puede apuntar en
  // los cobros del paciente, que es para lo que sirve todo esto.
  const [devolviendo, setDevolviendo] = useState(null);
  const [metodo, setMetodo] = useState('Efectivo');

  const cargar = useCallback(() => {
    const params = filtro === 'todas' ? {} : { estado: filtro };
    api.get('/api/devoluciones', { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        setItems([]);
        toast.error(e?.response?.data?.detail || 'No se pudieron cargar las devoluciones');
      });
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    if (!form.nombre.trim() || !form.apellido1.trim() || !form.apellido2.trim()) {
      return toast.error('Rellena nombre y los dos apellidos');
    }
    const importe = parseFloat(String(form.importe).replace(',', '.')) || 0;
    if (importe <= 0) return toast.error('Indica el importe a devolver');
    if (!form.motivo.trim()) return toast.error('Indica el motivo de la devolución');
    if (!form.forma_pago_original) return toast.error('Indica cómo pagó el paciente');
    if (form.forma_pago_original === 'Transferencia' && form.cuenta_origen.replace(/\s/g, '').length < 10) {
      return toast.error('Pagó por transferencia: indica el número de cuenta desde el que ingresó');
    }
    setBusy(true);
    try {
      await api.post('/api/devoluciones', {
        patient_id: form.patient_id || null,
        nombre: form.nombre.trim(),
        apellido1: form.apellido1.trim(),
        apellido2: form.apellido2.trim(),
        importe,
        motivo: form.motivo.trim(),
        forma_pago_original: form.forma_pago_original,
        cuenta_origen: form.cuenta_origen.trim() || null,
      });
      toast.success('Devolución apuntada · dirección queda avisada');
      setAlta(false);
      setForm(FORM_VACIO);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  const confirmarDevuelta = async () => {
    if (!devolviendo) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/api/devoluciones/${devolviendo.id}`, { estado: 'hecha', metodo });
      toast.success(
        data?.cobro_id
          ? `Devuelto a ${devolviendo.patient_name} por ${metodo} · apuntado en sus cobros como devolución`
          : `Devuelto a ${devolviendo.patient_name} por ${metodo}`,
      );
      setDevolviendo(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo marcar como devuelta');
    } finally { setBusy(false); }
  };

  const volverAPendiente = async (d) => {
    try {
      await api.put(`/api/devoluciones/${d.id}`, { estado: 'pendiente' });
      toast.success('Devuelta a pendiente · retirada de sus cobros');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  const abrirEdicion = (d) => {
    setEditando(d);
    setEdit({
      patient_id: d.patient_id || '',
      patient_name: d.patient_name || '',
      importe: d.importe == null ? '' : String(d.importe),
      motivo: d.motivo || '',
      forma_pago_original: d.forma_pago_original || '',
      cuenta_origen: d.cuenta_origen || '',
    });
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    // Las mismas comprobaciones que al darla de alta: corregir no puede dejar
    // la ficha peor de lo que la dejaría crearla. El servidor las repite.
    if (!edit.patient_id && !edit.patient_name.trim()) {
      return toast.error('Indica el paciente (nombre y apellidos)');
    }
    const importe = parseFloat(String(edit.importe).replace(',', '.')) || 0;
    if (importe <= 0) return toast.error('Indica el importe a devolver');
    if (!edit.motivo.trim()) return toast.error('Indica el motivo de la devolución');
    if (!edit.forma_pago_original) return toast.error('Indica cómo pagó el paciente');
    if (edit.forma_pago_original === 'Transferencia' && edit.cuenta_origen.replace(/\s/g, '').length < 10) {
      return toast.error('Pagó por transferencia: indica el número de cuenta desde el que ingresó');
    }
    const cuerpo = {
      importe,
      motivo: edit.motivo.trim(),
      forma_pago_original: edit.forma_pago_original,
      cuenta_origen: edit.cuenta_origen.trim() || null,
    };
    // La ficha solo se manda si CAMBIA, y con ficha enlazada el nombre no se
    // manda: lo pone la ficha (el servidor rechaza uno que no case con ella).
    // Así el dinero no se puede quedar colgado del paciente anterior.
    if ((edit.patient_id || '') !== (editando.patient_id || '')) {
      cuerpo.patient_id = edit.patient_id || null;
    }
    if (!edit.patient_id) cuerpo.patient_name = edit.patient_name.trim();
    setBusy(true);
    try {
      await api.put(`/api/devoluciones/${editando.id}`, cuerpo);
      toast.success(
        editando.estado === 'hecha'
          ? 'Devolución corregida · también su apunte en los cobros del paciente'
          : 'Devolución corregida',
      );
      setEditando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo corregir');
    } finally { setBusy(false); }
  };

  // Qué le va a pasar al dinero si se guarda la corrección. Se lee ANTES de
  // guardar, no después en la ficha del paciente (2026-09-17): cambiar de
  // ficha mueve el apunte negativo, y quitarla lo retira.
  const avisoDinero = (() => {
    if (!editando || editando.estado !== 'hecha') return null;
    if ((edit.patient_id || '') === (editando.patient_id || '')) return null;
    const apunte = formatEUR(-Math.abs(editando.importe || 0));
    if (!edit.patient_id) {
      return `Esto quita dinero de una ficha: se retirará de la de ${editando.patient_name} el apunte de ${apunte}. Queda escrito en Control de rastreo.`;
    }
    if (!editando.patient_id) {
      return `Esto apunta dinero en una ficha: se anotarán ${apunte} en la de ${edit.patient_name}.`;
    }
    return `Esto mueve dinero: los ${apunte} apuntados en la ficha de ${editando.patient_name} pasarán a la de ${edit.patient_name}.`;
  })();

  const confirmarBorrado = async () => {
    if (!borrando) return;
    setBusy(true);
    try {
      await api.delete(`/api/devoluciones/${borrando.id}`);
      toast.success('Devolución eliminada · queda en Control de rastreo qué era');
      setBorrando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setAlta(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="devolucion-nueva">
          <Plus className="w-4 h-4 mr-2" /> Apuntar devolución
        </Button>
      </div>
      <FiltroBarra filtro={filtro} setFiltro={setFiltro} testid="devoluciones-filtro" />

      <div className="cp-card overflow-hidden">
        {items === null ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={Undo2} title="Sin devoluciones" description={filtro === 'pendiente' ? 'No hay devoluciones pendientes.' : 'Nada por aquí.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Paciente</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Importe</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Motivo</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Pagó por</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Apuntada</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="devolucion-fila">
                    <td className="py-2.5 px-4 font-medium">{d.patient_name}</td>
                    <td className="py-2.5 px-4 text-right tabular-nums font-semibold">{d.importe ? formatEUR(d.importe) : '—'}</td>
                    <td className="py-2.5 px-4 text-xs max-w-[240px]">{d.motivo || '—'}</td>
                    <td className="py-2.5 px-4 text-xs">
                      {d.forma_pago_original || '—'}
                      {d.cuenta_origen && (
                        <div className="text-muted-foreground font-mono text-[11px] flex items-center gap-1 mt-0.5">
                          <Landmark className="w-3 h-3 shrink-0" /> {d.cuenta_origen}
                        </div>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-xs text-muted-foreground">
                      {formatDate((d.created_at || '').slice(0, 10))} · {d.created_by_name}
                    </td>
                    <td className="py-2.5 px-4">
                      {d.estado === 'hecha' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Devuelto{d.metodo ? ` · ${d.metodo}` : ''} {d.hecha_at ? `· ${formatDate(d.hecha_at.slice(0, 10))}` : ''}{d.hecha_by_name ? ` · ${d.hecha_by_name}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {d.estado === 'pendiente' ? (
                          <Button size="sm" variant="outline" className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => { setDevolviendo(d); setMetodo(d.forma_pago_original || 'Efectivo'); }} data-testid="devolucion-hecha">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Devuelto
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8" title="Volver a pendiente"
                            onClick={() => volverAPendiente(d)}>
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {/* Corregir y eliminar: quien la apunta puede arreglarla
                            (2026-09-17). Ya no se esconden a quien no es dirección. */}
                        <Button size="sm" variant="ghost" className="h-8" title="Corregir"
                          onClick={() => abrirEdicion(d)} data-testid="devolucion-editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-destructive" title="Eliminar"
                          onClick={() => setBorrando(d)} data-testid="devolucion-borrar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={alta} onOpenChange={setAlta}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Apuntar devolución</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Paciente de la base (recomendado)</Label>
              {/* Con la ficha enlazada, la devolución aparece luego en SUS
                  cobros; al elegirla se rellenan nombre y apellidos solos. */}
              <PatientCombobox
                value={form.patient_id}
                onChange={(id, ficha) => setForm((f) => ({
                  ...f,
                  patient_id: id,
                  // Con ficha elegida, los tres campos se sobreescriben SIEMPRE
                  // (aunque vengan vacíos): antes el 2º apellido del paciente
                  // anterior se quedaba pegado (revisión 2026-08-27).
                  nombre: ficha ? (ficha.name || '') : f.nombre,
                  apellido1: ficha ? (ficha.surname1 || '') : f.apellido1,
                  apellido2: ficha ? (ficha.surname2 || '') : f.apellido2,
                }))}
                dataTestid="devolucion-paciente"
              />
              {/* Con ficha enlazada el nombre no se retoca a mano: de esa
                  ficha colgará el apunte de -X € cuando se marque como
                  devuelta, y un nombre distinto del de la ficha manda el
                  dinero a un paciente y la devolución a otro (2026-09-17). */}
              {form.patient_id ? (
                <div className="mt-1 flex items-start justify-between gap-2">
                  <p className="text-xs text-muted-foreground">
                    El nombre lo pone la ficha: de ella colgará el apunte al devolver.
                  </p>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs shrink-0"
                    onClick={() => setForm((f) => ({ ...f, patient_id: '' }))}
                    data-testid="devolucion-quitar-ficha">
                    Quitar la ficha
                  </Button>
                </div>
              ) : null}
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label>Nombre*</Label>
                <Input value={form.nombre} disabled={!!form.patient_id}
                  onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                  data-testid="devolucion-nombre" />
              </div>
              <div>
                <Label>1er apellido*</Label>
                <Input value={form.apellido1} disabled={!!form.patient_id}
                  onChange={(e) => setForm((f) => ({ ...f, apellido1: e.target.value }))}
                  data-testid="devolucion-apellido1" />
              </div>
              <div>
                <Label>2º apellido*</Label>
                <Input value={form.apellido2} disabled={!!form.patient_id}
                  onChange={(e) => setForm((f) => ({ ...f, apellido2: e.target.value }))}
                  data-testid="devolucion-apellido2" />
              </div>
            </div>
            <div>
              <Label>Importe a devolver (€)*</Label>
              <Input type="text" inputMode="decimal" value={form.importe}
                onChange={(e) => setForm((f) => ({ ...f, importe: e.target.value }))}
                placeholder="150" data-testid="devolucion-importe" />
            </div>
            <div>
              <Label>Motivo de la devolución*</Label>
              <Textarea value={form.motivo} onChange={(e) => setForm((f) => ({ ...f, motivo: e.target.value }))}
                placeholder="Anulación de tratamiento, cobro duplicado…" data-testid="devolucion-motivo" />
            </div>
            <div>
              <Label>¿Cómo pagó el paciente?*</Label>
              <Select value={form.forma_pago_original || undefined}
                onValueChange={(v) => setForm((f) => ({ ...f, forma_pago_original: v }))}>
                <SelectTrigger data-testid="devolucion-forma-pago"><SelectValue placeholder="Forma del pago original…" /></SelectTrigger>
                <SelectContent>
                  {METODOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {form.forma_pago_original === 'Transferencia' && (
              <div>
                <Label>Cuenta desde la que ingresó el paciente*</Label>
                <Input value={form.cuenta_origen}
                  onChange={(e) => setForm((f) => ({ ...f, cuenta_origen: e.target.value }))}
                  placeholder="ES00 0000 0000 0000 0000 0000"
                  className="font-mono" data-testid="devolucion-cuenta" />
                <p className="text-xs text-muted-foreground mt-1">
                  Se le devolverá a esa misma cuenta.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlta(false)}>Cancelar</Button>
            <Button onClick={crear} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="devolucion-guardar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Marcar como devuelta: el método es obligatorio y viene precargado con
          la forma en que pagó el paciente. */}
      <Dialog open={!!devolviendo} onOpenChange={(v) => !v && setDevolviendo(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>¿Cómo se ha devuelto?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">
              <span className="font-medium">{devolviendo?.patient_name}</span>
              {devolviendo?.importe ? <> · <span className="tabular-nums font-semibold">{formatEUR(devolviendo.importe)}</span></> : null}
            </div>
            {devolviendo?.forma_pago_original === 'Transferencia' && devolviendo?.cuenta_origen && (
              <div className="rounded-lg border border-[#2E6E5E]/30 bg-[rgba(46,110,94,0.06)] px-3 py-2 text-sm">
                <div className="text-xs font-semibold text-[#13231E] mb-0.5 flex items-center gap-1">
                  <Landmark className="w-3.5 h-3.5" /> Devolver a la cuenta del ingreso
                </div>
                <div className="font-mono text-[13px]">{devolviendo.cuenta_origen}</div>
              </div>
            )}
            <div>
              <Label>Método de devolución</Label>
              <Select value={metodo} onValueChange={setMetodo}>
                <SelectTrigger data-testid="devolucion-metodo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {METODOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              {devolviendo?.patient_id
                ? 'Quedará apuntado en los cobros del paciente con la fecha de hoy, en negativo y marcado como devolución.'
                : 'Este paciente no tiene ficha enlazada, así que no se podrá apuntar en sus cobros. Dirección recibe el aviso igualmente.'}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDevolviendo(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={confirmarDevuelta} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="devolucion-confirmar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Marcar como devuelto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corregir la devolución (2026-09-17): los mismos datos que se piden al
          apuntarla. Si ya estaba devuelta, el servidor corrige además el
          apunte negativo que lleva en la ficha del paciente. */}
      <Dialog open={!!editando} onOpenChange={(v) => !v && setEditando(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Corregir devolución</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* EL PACIENTE (2026-09-17, segunda vuelta). Antes esto era un
                campo de texto y nunca mandaba `patient_id`: se reescribía el
                nombre y el importe seguía colgando de la ficha anterior.
                Ahora se cambia de paciente ELIGIENDO otra ficha —y el apunte
                se va con ella— o quitándola a propósito, avisando de que el
                apunte se retira. */}
            <div>
              <Label>Paciente*</Label>
              <PatientCombobox
                value={edit.patient_id}
                onChange={(id, ficha) => setEdit((f) => ({
                  ...f,
                  patient_id: id,
                  patient_name: ficha ? (fullName(ficha) || ficha.name || f.patient_name) : f.patient_name,
                }))}
                placeholder={edit.patient_id ? 'Selecciona paciente…' : 'Sin ficha: elige una para enlazarla…'}
                dataTestid="devolucion-editar-ficha"
              />
              {edit.patient_id ? (
                <div className="mt-1 flex items-start justify-between gap-2">
                  <p className="text-xs text-muted-foreground" data-testid="devolucion-editar-nombre-ficha">
                    El nombre lo pone la ficha: <strong>{edit.patient_name}</strong>
                  </p>
                  <Button size="sm" variant="ghost" className="h-6 px-2 text-xs shrink-0"
                    onClick={() => setEdit((f) => ({ ...f, patient_id: '' }))}
                    data-testid="devolucion-editar-quitar-ficha">
                    Quitar la ficha
                  </Button>
                </div>
              ) : (
                <Input className="mt-1.5" value={edit.patient_name}
                  onChange={(e) => setEdit((f) => ({ ...f, patient_name: e.target.value }))}
                  placeholder="Nombre y apellidos"
                  data-testid="devolucion-editar-paciente" />
              )}
              {avisoDinero && (
                <div className="mt-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                  data-testid="devolucion-editar-aviso-dinero">
                  {avisoDinero}
                </div>
              )}
            </div>
            <div>
              <Label>Importe a devolver (€)*</Label>
              <Input type="text" inputMode="decimal" value={edit.importe}
                onChange={(e) => setEdit((f) => ({ ...f, importe: e.target.value }))}
                data-testid="devolucion-editar-importe" />
            </div>
            <div>
              <Label>Motivo de la devolución*</Label>
              <Textarea value={edit.motivo}
                onChange={(e) => setEdit((f) => ({ ...f, motivo: e.target.value }))}
                data-testid="devolucion-editar-motivo" />
            </div>
            <div>
              <Label>¿Cómo pagó el paciente?*</Label>
              <Select value={edit.forma_pago_original || undefined}
                onValueChange={(v) => setEdit((f) => ({ ...f, forma_pago_original: v }))}>
                <SelectTrigger data-testid="devolucion-editar-forma-pago"><SelectValue placeholder="Forma del pago original…" /></SelectTrigger>
                <SelectContent>
                  {METODOS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {edit.forma_pago_original === 'Transferencia' && (
              <div>
                <Label>Cuenta desde la que ingresó el paciente*</Label>
                <Input value={edit.cuenta_origen}
                  onChange={(e) => setEdit((f) => ({ ...f, cuenta_origen: e.target.value }))}
                  placeholder="ES00 0000 0000 0000 0000 0000"
                  className="font-mono" data-testid="devolucion-editar-cuenta" />
              </div>
            )}
            {editando?.estado === 'hecha' && (
              <p className="text-xs text-muted-foreground">
                Esta devolución ya está marcada como devuelta: al corregirla se corrige también
                el apunte negativo que tiene en los cobros del paciente.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={guardarEdicion} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="devolucion-editar-guardar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmarBorrado
        item={borrando}
        testid="devolucion-borrado"
        titulo="Eliminar devolución"
        importe={borrando?.importe}
        concepto={borrando?.motivo}
        aviso={borrando?.estado === 'hecha'
          ? 'Ya estaba marcada como devuelta: al eliminarla se retira también el apunte negativo de los cobros del paciente.'
          : null}
        busy={busy}
        onCancel={() => setBorrando(null)}
        onConfirm={confirmarBorrado}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Solicitudes de factura (2026-08-27): secretaría apunta quién pide factura y
// con qué datos; dirección las ve pendientes y las marca emitidas.
// ---------------------------------------------------------------------------
function TabFacturas() {
  const [items, setItems] = useState(null);
  const [filtro, setFiltro] = useState('pendiente');
  const [alta, setAlta] = useState(false);
  const FORM_VACIO = { patient_id: '', patient_name: '', datos_fiscales: '', importe: '', notas: '' };
  const [form, setForm] = useState(FORM_VACIO);
  const [busy, setBusy] = useState(false);
  // Corregir una solicitud mal apuntada (2026-09-17): un NIF con una
  // letra cambiada obligaba a borrarla —y borrar era de dirección—, así que se
  // quedaba mal en el sistema.
  const [editando, setEditando] = useState(null);
  const [edit, setEdit] = useState({ patient_name: '', datos_fiscales: '', importe: '', notas: '' });
  const [borrando, setBorrando] = useState(null);

  const cargar = useCallback(() => {
    const params = filtro === 'todas' ? {} : { estado: filtro === 'hecha' ? 'emitida' : filtro };
    api.get('/api/devoluciones/facturas/solicitudes', { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        setItems([]);
        toast.error(e?.response?.data?.detail || 'No se pudieron cargar las solicitudes');
      });
  }, [filtro]);

  useEffect(() => { cargar(); }, [cargar]);

  const crear = async () => {
    if (!form.patient_name.trim()) return toast.error('Indica el paciente');
    if (!form.datos_fiscales.trim()) return toast.error('Indica los datos para la factura (NIF, dirección fiscal…)');
    setBusy(true);
    try {
      await api.post('/api/devoluciones/facturas/solicitudes', {
        patient_id: form.patient_id || null,
        patient_name: form.patient_name.trim(),
        datos_fiscales: form.datos_fiscales.trim(),
        importe: parseFloat(String(form.importe).replace(',', '.')) || 0,
        notas: form.notas.trim(),
      });
      toast.success('Solicitud de factura apuntada · dirección queda avisada');
      setAlta(false);
      setForm(FORM_VACIO);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar');
    } finally { setBusy(false); }
  };

  const cambiarEstado = async (sol, estado) => {
    try {
      await api.put(`/api/devoluciones/facturas/solicitudes/${sol.id}`, { estado });
      toast.success(estado === 'emitida' ? `Factura de ${sol.patient_name} marcada como emitida` : 'Devuelta a pendiente');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cambiar');
    }
  };

  const abrirEdicion = (sol) => {
    setEditando(sol);
    setEdit({
      patient_name: sol.patient_name || '',
      datos_fiscales: sol.datos_fiscales || '',
      importe: sol.importe == null ? '' : String(sol.importe),
      notas: sol.notas || '',
    });
  };

  const guardarEdicion = async () => {
    if (!editando) return;
    // Mismas comprobaciones que el alta: una factura sin NIF no se emite.
    if (!edit.patient_name.trim()) return toast.error('Indica el paciente');
    if (!edit.datos_fiscales.trim()) return toast.error('Indica los datos para la factura (NIF, dirección fiscal…)');
    setBusy(true);
    try {
      await api.put(`/api/devoluciones/facturas/solicitudes/${editando.id}`, {
        patient_name: edit.patient_name.trim(),
        datos_fiscales: edit.datos_fiscales.trim(),
        importe: parseFloat(String(edit.importe).replace(',', '.')) || 0,
        notas: edit.notas.trim(),
      });
      toast.success('Solicitud de factura corregida');
      setEditando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo corregir');
    } finally { setBusy(false); }
  };

  const confirmarBorrado = async () => {
    if (!borrando) return;
    setBusy(true);
    try {
      await api.delete(`/api/devoluciones/facturas/solicitudes/${borrando.id}`);
      toast.success('Solicitud eliminada · queda en Control de rastreo qué era');
      setBorrando(null);
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex justify-end mb-3">
        <Button onClick={() => setAlta(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="factura-nueva">
          <Plus className="w-4 h-4 mr-2" /> Apuntar solicitud de factura
        </Button>
      </div>
      <FiltroBarra
        filtro={filtro} setFiltro={setFiltro}
        opciones={[['pendiente', 'Pendientes'], ['hecha', 'Emitidas'], ['todas', 'Todas']]}
        testid="facturas-filtro"
      />

      <div className="cp-card overflow-hidden">
        {items === null ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={ReceiptText} title="Sin solicitudes de factura" description={filtro === 'pendiente' ? 'No hay facturas pendientes de emitir.' : 'Nada por aquí.'} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Paciente</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Datos para la factura</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E] text-right">Importe</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Apuntada</th>
                  <th className="py-2.5 px-4 font-semibold text-[#13231E]">Estado</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((sol) => (
                  <tr key={sol.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="factura-fila">
                    <td className="py-2.5 px-4 font-medium">{sol.patient_name}</td>
                    <td className="py-2.5 px-4 text-xs max-w-[320px] whitespace-pre-line">
                      {sol.datos_fiscales}
                      {sol.notas ? <div className="text-muted-foreground mt-0.5">{sol.notas}</div> : null}
                    </td>
                    <td className="py-2.5 px-4 text-right tabular-nums font-semibold">{sol.importe ? formatEUR(sol.importe) : '—'}</td>
                    <td className="py-2.5 px-4 text-xs text-muted-foreground">
                      {formatDate((sol.created_at || '').slice(0, 10))} · {sol.created_by_name}
                    </td>
                    <td className="py-2.5 px-4">
                      {sol.estado === 'emitida' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Emitida {sol.emitida_at ? `· ${formatDate(sol.emitida_at.slice(0, 10))}` : ''}{sol.emitida_by_name ? ` · ${sol.emitida_by_name}` : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5">
                          Pendiente
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {sol.estado === 'pendiente' ? (
                          <Button size="sm" variant="outline" className="h-8 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => cambiarEstado(sol, 'emitida')} data-testid="factura-emitida">
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Emitida
                          </Button>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-8" title="Volver a pendiente"
                            onClick={() => cambiarEstado(sol, 'pendiente')}>
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-8" title="Corregir"
                          onClick={() => abrirEdicion(sol)} data-testid="factura-editar">
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 text-destructive" title="Eliminar"
                          onClick={() => setBorrando(sol)} data-testid="factura-borrar">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={alta} onOpenChange={setAlta}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Apuntar solicitud de factura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Paciente*</Label>
              <PatientCombobox
                value={form.patient_id}
                onChange={(id, ficha) => setForm((f) => ({
                  ...f,
                  patient_id: id,
                  patient_name: ficha ? (fullName(ficha) || ficha.name || f.patient_name) : f.patient_name,
                }))}
                dataTestid="factura-paciente"
              />
              <Input
                className="mt-1.5"
                value={form.patient_name}
                onChange={(e) => setForm((f) => ({ ...f, patient_name: e.target.value }))}
                placeholder="…o escribe el nombre a mano si no tiene ficha"
                data-testid="factura-paciente-nombre"
              />
            </div>
            <div>
              <Label>Datos para la factura*</Label>
              <Textarea
                value={form.datos_fiscales}
                onChange={(e) => setForm((f) => ({ ...f, datos_fiscales: e.target.value }))}
                placeholder={'NIF, nombre fiscal y dirección…\nEj.: 12345678A · C/ Mayor 1, 28001 Madrid'}
                data-testid="factura-datos"
              />
            </div>
            <div>
              <Label>Importe (€, opcional)</Label>
              <Input type="text" inputMode="decimal" value={form.importe}
                onChange={(e) => setForm((f) => ({ ...f, importe: e.target.value }))}
                placeholder="150" data-testid="factura-importe" />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                placeholder="Concepto, cómo la quiere recibir…" data-testid="factura-notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlta(false)}>Cancelar</Button>
            <Button onClick={crear} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="factura-guardar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Corregir la solicitud (2026-09-17): paciente, datos fiscales, importe
          y notas, que es todo lo que se apunta al darla de alta. */}
      <Dialog open={!!editando} onOpenChange={(v) => !v && setEditando(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Corregir solicitud de factura</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Paciente*</Label>
              <Input value={edit.patient_name}
                onChange={(e) => setEdit((f) => ({ ...f, patient_name: e.target.value }))}
                data-testid="factura-editar-paciente" />
            </div>
            <div>
              <Label>Datos para la factura*</Label>
              <Textarea value={edit.datos_fiscales}
                onChange={(e) => setEdit((f) => ({ ...f, datos_fiscales: e.target.value }))}
                data-testid="factura-editar-datos" />
            </div>
            <div>
              <Label>Importe (€, opcional)</Label>
              <Input type="text" inputMode="decimal" value={edit.importe}
                onChange={(e) => setEdit((f) => ({ ...f, importe: e.target.value }))}
                data-testid="factura-editar-importe" />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input value={edit.notas}
                onChange={(e) => setEdit((f) => ({ ...f, notas: e.target.value }))}
                data-testid="factura-editar-notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditando(null)} disabled={busy}>Cancelar</Button>
            <Button onClick={guardarEdicion} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="factura-editar-guardar">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmarBorrado
        item={borrando}
        testid="factura-borrado"
        titulo="Eliminar solicitud de factura"
        importe={borrando?.importe}
        concepto={borrando?.datos_fiscales}
        busy={busy}
        onCancel={() => setBorrando(null)}
        onConfirm={confirmarBorrado}
      />
    </>
  );
}

/**
 * Facturas y devoluciones (2026-08-27): un solo apartado donde
 * secretaría deja apuntadas las solicitudes de factura y las devoluciones
 * pendientes, y dirección las gestiona. Al marcar una devolución como hecha
 * se apunta sola en los cobros del paciente, en negativo y como devolución.
 *
 * 2026-09-17: «si se comete un error al registrar una factura o una
 * devolución, no es posible ni corregirla ni borrarla». Lo de corregir no era
 * un permiso —el servidor ya lo dejaba a gestión desde el 2026-07-28—: es que
 * esta pantalla NUNCA tuvo botón de editar. Y borrar estaba escondido a quien
 * no fuese dirección. Ahora ambas cosas están a la vista para quien apunta la
 * ficha, y el borrado pide confirmación con el importe delante.
 */
export default function DevolucionesPage() {
  return (
    <div>
      <PageHeader
        title="Facturas y devoluciones"
        subtitle="Solicitudes de factura y devoluciones pendientes. Quien las apunta puede corregirlas y eliminarlas; todo queda en Control de rastreo."
      />
      <Tabs defaultValue="facturas">
        <TabsList>
          <TabsTrigger value="facturas" data-testid="tab-facturas">
            <ReceiptText className="w-4 h-4 mr-1" /> Facturas
          </TabsTrigger>
          <TabsTrigger value="devoluciones" data-testid="tab-devoluciones">
            <Undo2 className="w-4 h-4 mr-1" /> Devoluciones
          </TabsTrigger>
        </TabsList>
        <TabsContent value="facturas" className="mt-4">
          <TabFacturas />
        </TabsContent>
        <TabsContent value="devoluciones" className="mt-4">
          <TabDevoluciones />
        </TabsContent>
      </Tabs>
    </div>
  );
}
